/* -----------------Components--------------- */
import Box from "@memorang/ui/src/components/Box";

import type { WelcomeDialogSchemaType } from "@configs/custom-app-config-schema";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import { useCallback, useRef, useState } from "react";
import { ScrollView } from "react-native";
import { getSessionStartDialogConfig } from "../constants/dialog-configs";
import { PassageStemModalContainer } from "../containers/PassageStemModalContainer";
import StudySessionBottombarContainer from "../containers/StudySessionBottombarContainer";
/* -----------------Containers--------------- */
import StudySessionFeedbackContainer from "../containers/StudySessionFeedbackContainer";
import useSessionStore from "../hooks/useSessionStore";
import { useStartDialogStore } from "../hooks/useShowStartDialog";
import DialogSession from "./SessionDialog";
import SessionListDesktop from "./SessionList/SessionListDesktop";
import { StudySessionAppBarContainer } from "./StudySessionAppBarContainer";

type Props = {
	sessionItems: BaseItemSchema[];
	sessionId: number;
	currentBlockIndex: number;
	totalTime: number;
	inReviewMode?: boolean;
	reportSessionType?: "PRACTICE_TEST" | "DIAGNOSTIC";
	numSections: number;
	showDisclaimer: boolean;
	numTotalItems: number;
};

const RenderStartDialog = ({
	examName,
	totalTime,
	reportSessionType,
	showStartDialog,
	handleCloseStartDialog,
	numSections,
	welcomeDialog,
	numTotalItems,
}: {
	examName?: string;
	totalTime: number;
	reportSessionType?: "PRACTICE_TEST" | "DIAGNOSTIC";
	showStartDialog: boolean;
	handleCloseStartDialog: () => void;
	numSections: number;
	welcomeDialog?: WelcomeDialogSchemaType;
	numTotalItems: number;
}) => {
	const diagnosticLabel = useAppStore((state) => state.exam?.diagnosticLabel);

	if (showStartDialog) {
		const numTotalQuestions = numTotalItems;

		const customConfig = getSessionStartDialogConfig({
			examName: examName || "",
			totalTime,
			isQuiz: reportSessionType === "DIAGNOSTIC",
			numSections,
			numTotalQuestions,
			diagnosticLabel,
			welcomeDialog,
		});
		return (
			<DialogSession
				customConfig={customConfig}
				open
				handleClose={handleCloseStartDialog}
				handleClickButton={handleCloseStartDialog}
			/>
		);
	}
	return null;
};

const PracticeSessionView = ({
	sessionItems,
	totalTime,
	inReviewMode,
	reportSessionType,
	numSections,
	showDisclaimer,
	numTotalItems,
}: Props) => {
	const {
		currentExamContext: { examId },
	} = useExamContext();

	const welcomeDialog = useAppStore(
		(state) => state.sessionView?.welcomeDialog,
	);
	const [showReadMore, setShowReadMore] = useState(false);

	const toggleShowReadMore = useCallback(() => {
		setShowReadMore((prev) => !prev);
	}, []);

	const { isMobile } = useDevice();
	const startDialogShownKeys = useStartDialogStore(
		(store) => store.startDialogShownKeys,
	);
	const setStartDialogShown = useStartDialogStore(
		(store) => store.setStartDialogShown,
	);
	const passageStemModalRef = useRef<{ scrollToHighlightedText: () => void }>(
		null,
	);
	const examName = useSessionStore((state) => state.examName);

	const theme = useAppTheme();

	const showStartDialog = !(
		startDialogShownKeys?.[examId] ||
		inReviewMode ||
		showDisclaimer
	);

	const finalSessionItems = sessionItems;

	const handleCloseStartDialog = () => {
		setStartDialogShown(String(examId));
	};

	return (
		<Box
			gap={isMobile ? 8 : 0}
			flex={1}
			backgroundColor={theme.colors.background}
		>
			<BottomSheetModalProvider>
				<Box flex={1}>
					<StudySessionAppBarContainer
						showStartDialog={showStartDialog || showDisclaimer}
					/>
					<ScrollView
						contentContainerStyle={{
							flexGrow: 1,
							height: "100%",
						}}
					>
						<Box
							flex={1}
							height={"100%"}
							backgroundColor={theme.colors.surface}
							accessibilityLabel="Session View, containing the question, and available options."
							// biome-ignore lint/a11y/useSemanticElements: <explanation>
							role="main"
						>
							<SessionListDesktop
								items={finalSessionItems}
								toggleShowReadMore={toggleShowReadMore}
							/>
							{!isWeb && <StudySessionFeedbackContainer />}
						</Box>
					</ScrollView>
				</Box>
			</BottomSheetModalProvider>
			<StudySessionBottombarContainer />

			<RenderStartDialog
				examName={examName}
				totalTime={totalTime}
				reportSessionType={reportSessionType}
				showStartDialog={showStartDialog}
				handleCloseStartDialog={handleCloseStartDialog}
				numSections={numSections}
				welcomeDialog={welcomeDialog}
				numTotalItems={numTotalItems}
			/>
			<PassageStemModalContainer
				passageStemModalRef={passageStemModalRef}
				showReadMore={showReadMore}
				propToggleShowReadMore={() => setShowReadMore(false)}
			/>
		</Box>
	);
};

export default PracticeSessionView;
